import { useEffect, useRef, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Autocomplete from "@/components/Autocomplete";
import MenuDropdown from "@/components/MenuDropdown";
import Dialog from "@/components/Dialog";
import ListItem from "@/components/ListItem";
import Banner from "@/components/Banner";
import classNames from "classnames";
import { SOME_USERS_QUERY, type BackofficeUserOption } from "@/pages/people/queries";
import type { UniversityStaffRole } from "./types";

interface AddStaffMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (userId: string, role: "DIRECTOR" | "STAFF") => void;
  isLoading?: boolean;
}

interface FormValues {
  role: "DIRECTOR" | "STAFF";
}

const ROLE_OPTIONS: Array<{
  value: UniversityStaffRole;
  label: string;
  description: string;
}> = [
  {
    value: "DIRECTOR",
    label: "Director",
    description:
      "Directors receive university event registration notifications in addition to roster, probation, and graduation emails."
  },
  {
    value: "STAFF",
    label: "Staff",
    description:
      "Staff receive roster finalized, probation, and graduation finalized notifications for this university."
  }
];

export function AddStaffMemberModal({ open, onClose, onSave, isLoading = false }: AddStaffMemberModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<BackofficeUserOption | null>(null);
  const autocompleteWrapperRef = useRef<HTMLDivElement>(null);

  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      role: "STAFF"
    }
  });

  const selectedRole = watch("role");

  const { data: usersData, loading: usersLoading } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm },
    skip: !debouncedSearchTerm
  });

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setSearchTerm("");
      setDebouncedSearchTerm("");
    }
  }, [open]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const syncMenuWidth = useCallback(() => {
    const wrapper = autocompleteWrapperRef.current;
    if (!wrapper) return;

    const mdMenu = wrapper.querySelector("md-menu") as HTMLElement | null;
    const anchor = wrapper.querySelector(".relative");

    if (mdMenu && anchor) {
      const width = anchor.getBoundingClientRect().width;
      if (width > 0) {
        mdMenu.style.minWidth = `${width}px`;
        mdMenu.style.width = `${width}px`;
        mdMenu.style.maxWidth = `${width}px`;
      }
    }
  }, []);

  useEffect(() => {
    if (usersData) {
      requestAnimationFrame(syncMenuWidth);
    }
  }, [usersData, syncMenuWidth]);

  const onSubmit = (values: FormValues) => {
    if (selectedUser && values.role) {
      onSave(selectedUser.id, values.role);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedUser(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClosed={handleClose}
      headline="Add Staff Member"
      className="sm:max-w-[480px]"
      hasFormElements
      displayOverflow
      actions={
        <>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || !selectedUser}
            icon={isLoading ? "progress_activity" : undefined}
            iconClassName={classNames(isLoading && "animate-spin w-6 h-6 !block")}
          >
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <p className="text-body-medium text-sys-on-surface">
          Assign a backoffice user to this university. Their role controls which university notification emails they
          receive.
        </p>

        <div ref={autocompleteWrapperRef}>
          <Autocomplete<BackofficeUserOption>
            autofocus
            label="User"
            data={usersData?.backofficeUsers || []}
            menuPositioning="fixed"
            loadingResults={usersLoading}
            query={selectedUser ? selectedUser.name : searchTerm}
            onChangeText={(value) => {
              if (selectedUser) {
                setSelectedUser(null);
              }
              setSearchTerm(value);
            }}
            onSelect={(user) => {
              setSelectedUser(user);
              setSearchTerm(user.name);
            }}
            reset={() => {
              setSelectedUser(null);
              setSearchTerm("");
              setDebouncedSearchTerm("");
            }}
            renderItem={(user) => (
              <ListItem
                compact
                useAvatar
                iconName="person"
                headline={user.name}
                supportingText={user.email || undefined}
              />
            )}
            variant="searchableDropdown"
            required
            placeholder="Search by name..."
            leadingIcon="search"
            menuClassName="max-h-[300px]"
            constrainMenuWidth={true}
          />
        </div>

        <Controller
          name="role"
          control={control}
          rules={{ required: "Role is required" }}
          render={({ field: { onChange, value }, fieldState }) => (
            <div>
              <MenuDropdown
                label="Role"
                options={ROLE_OPTIONS}
                value={ROLE_OPTIONS.find((option) => option.value === value)}
                onSelect={(option) => onChange(option.value)}
                getLabel={(option) => option.label}
                renderItem={(option) => <div className="text-body-large">{option.label}</div>}
                constrainMenuWidth
                disabled={isLoading}
              />
              {fieldState.invalid && fieldState.error?.message && (
                <div className="text-sys-brand-error text-body-small pt-1 pl-4">{fieldState.error.message}</div>
              )}
            </div>
          )}
        />

        {selectedRole && (
          <Banner
            variant="info"
            inline
            description={ROLE_OPTIONS.find((option) => option.value === selectedRole)?.description || ""}
          />
        )}
      </div>
    </Dialog>
  );
}
